import axios from "axios";

const API_BASE = process.env.REACT_APP_API_BASE || "https://blasterprime.com/api";

// Buat instance axios dengan konfigurasi default
const apiClient = axios.create({
  baseURL: API_BASE,
  timeout: 10000,
});

// Interceptor untuk menambahkan token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      console.log('🔐 Token attached to request:', config.url); // Debug log
    } else {
      console.warn('⚠️ No token found for request:', config.url);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Interceptor untuk handle response
apiClient.interceptors.response.use(
  (response) => {
    console.log('✅ API Success:', response.config.url, response.status);
    return response;
  },
  (error) => {
    console.error('❌ API Error:', {
      url: error.config?.url,
      status: error.response?.status,
      message: error.response?.data?.message || error.message
    });

    if (error.response?.status === 401 || error.response?.status === 403) {
      // Token expired atau invalid
      console.log('🚫 Token expired or invalid, redirecting to login...');
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      sessionStorage.removeItem("token");
      
      // Only redirect if not already on login page
      if (!window.location.pathname.includes('/login')) {
        window.location.href = "/login";
      }
    }
    return Promise.reject(error);
  },
);

// Fungsi fetchApi yang diperbaiki (menggunakan axios)
export const fetchApi = async (endpoint, options = {}) => {
  const url = `${API_BASE}${endpoint}`;
  const token = localStorage.getItem("token") || sessionStorage.getItem("token");

  console.log('📤 Fetch API Request:', {
    url,
    method: options.method || 'GET',
    hasToken: !!token
  });

  const headers = {
    ...(options.headers || {}),
  };

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  // Handle FormData
  if (options.body instanceof FormData) {
    delete headers["Content-Type"];
  } else if (options.body && typeof options.body === "object") {
    headers["Content-Type"] = "application/json";
    options.body = JSON.stringify(options.body);
  }

  try {
    const response = await fetch(url, {
      ...options,
      headers,
    });

    // Log response status
    console.log('📥 Fetch API Response:', {
      url,
      status: response.status,
      statusText: response.statusText
    });

    if (!response.ok) {
      let errorMessage = `Error ${response.status}: ${response.statusText}`;

      // Handle token expired
      if (response.status === 401 || response.status === 403) {
        console.log('🚫 Token expired or invalid (fetch), redirecting...');
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        sessionStorage.removeItem("token");
        
        if (!window.location.pathname.includes('/login')) {
          window.location.href = "/login";
        }
      }

      try {
        const errorData = await response.json();
        errorMessage = errorData.message || errorData.error || errorMessage;
      } catch (e) {
        // Biarkan errorMessage default
      }
      throw new Error(errorMessage);
    }

    const data = await response.json();
    console.log('✅ Fetch API Success:', endpoint);
    return data;
  } catch (error) {
    console.error("❌ Fetch API error:", error);
    throw error;
  }
};

// === Referral APIs (Admin) ===
// === Referral APIs (Admin) - Perbaikan dengan error handling ===
export const getAllReferrals = async () => {
  try {
    // Coba beberapa kemungkinan endpoint
    const possibleEndpoints = [
      '/referrals/all',           // Endpoint yang digunakan sekarang
      '/referrals',               // Alternatif 1
      '/referrals/admin/all',      // Alternatif 2
      '/referrals/list'            // Alternatif 3
    ];
    
    let lastError = null;
    
    // Coba setiap endpoint
    for (const endpoint of possibleEndpoints) {
      try {
        console.log(`Trying endpoint: ${endpoint}`);
        const response = await apiClient.get(endpoint);
        console.log(`Success with endpoint: ${endpoint}`);
        return response; // Return jika berhasil
      } catch (err) {
        lastError = err;
        console.log(`Endpoint ${endpoint} failed:`, err.message);
        // Lanjut ke endpoint berikutnya
      }
    }
    
    // Jika semua endpoint gagal, return empty array dengan format yang benar
    console.warn('All referral endpoints failed, returning empty array');
    return { data: [] };
  } catch (error) {
    console.error('Error in getAllReferrals:', error);
    // Return empty array dengan format yang benar agar tidak crash
    return { data: [] };
  }
};
export const getReferralRoles = () => apiClient.get("/referrals/admin/roles");
export const createReferralRole = (data) => apiClient.post("/referrals/admin/roles", data);
export const updateReferralRole = (id, data) => apiClient.put(`/referrals/admin/roles/${id}`, data);
export const deleteReferralRole = (id) => apiClient.delete(`/referrals/admin/roles/${id}`);
export const setDefaultRole = (id) => apiClient.post(`/referrals/admin/roles/${id}/set-default`);

// === Profile APIs (disesuaikan dengan profile.routes.js) ===

// Ambil profil user login
export const getProfile = () => apiClient.get("/profile");

// Update profil user
export const updateProfile = async (data) => {
  if (data instanceof FormData) {
    return apiClient.put("/profile", data, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  }
  return apiClient.put("/profile", data);
};

// Ganti password user
export const changePassword = (data) => apiClient.post("/profile/change-password", data);

// === Commission Settings APIs ===
export const createCommissionSetting = (data) => apiClient.post("/referrals/admin/settings", data);
export const getCommissionSettings = () => apiClient.get("/referrals/admin/settings");
export const setActiveCommissionSetting = (id) => apiClient.post(`/referrals/admin/settings/${id}/activate`);
export const getCommissionSettingById = (id) => apiClient.get(`/referrals/admin/settings/${id}`);
export const updateCommissionSetting = (id, data) => apiClient.put(`/referrals/admin/settings/${id}`, data);
export const deleteCommissionSetting = (id) => apiClient.delete(`/referrals/admin/settings/${id}`);

// === Support APIs ===
export const createTicket = (data) => {
  if (data instanceof FormData) {
    return apiClient.post("/support/tickets", data, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  }
  return apiClient.post("/support/tickets", data);
};

export const sendEmailResponse = (ticketId, responseContent) =>
  apiClient.post(`/support/tickets/${ticketId}/respond`, {
    response: responseContent,
    via: "email",
    action: "reply",
  });

export const sendTelegramResponse = (ticketId, phoneNumber, responseContent) =>
  apiClient.post(`/support/tickets/${ticketId}/respond`, {
    phoneNumber,
    response: responseContent,
    via: "telegram",
    action: "reply",
  });

// === Dashboard Counts API ===
export const getSidebarCounts = () => apiClient.get("/sidebar/sidebar-counts");

// Export apiClient untuk penggunaan langsung
export default apiClient;